<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { useThemeVars } from 'naive-ui';
import type { Config, Data, Layout } from 'plotly.js';
import { statisticsChartSeriesColors } from '@/utils/adminStatistics';

interface Props {
  title: string;
  rows: [string, number][];
  xLabel: string;
  yLabel: string;
  horizontal?: boolean;
  height?: string;
  color?: string;
  emptyDescription?: string;
  formatCategory?: (value: string) => string;
  formatValue?: (value: number) => string;
  /** Fixed value-axis ticks (e.g. named scale steps) instead of auto numbers. */
  valueTicks?: { tickvals: number[]; ticktext: string[] };
}

const props = withDefaults(defineProps<Props>(), {
  horizontal: false,
  height: undefined,
  color: undefined,
  emptyDescription: 'No data',
  formatCategory: (value: string) => value,
  formatValue: (value: number) => value.toLocaleString(),
  valueTicks: undefined,
});

// Plotly supports rounded bar ends since v2.25 but @types/plotly.js has no field for it.
type BarLayout = Partial<Layout> & { barcornerradius?: number };

const MAX_CATEGORY_LABEL_LENGTH = 26;
const HORIZONTAL_ROW_HEIGHT = 34;

const PlotlyPlot = defineAsyncComponent(() => import('@/components/charts/PlotlyPlot.vue'));
const themeVars = useThemeVars();

const barColor = computed(() => props.color ?? statisticsChartSeriesColors[0]);

const fullCategories = computed(() =>
  props.rows.map(([category]) => props.formatCategory(category))
);

// Tick labels are truncated so long categories (e.g. MIME types) never blow out
// the axis margin; the full name stays available in the hover tooltip. Zero-width
// spaces keep truncated duplicates unique without being visible.
const tickCategories = computed(() => {
  const seen = new Map<string, number>();

  return fullCategories.value.map(label => {
    const truncated =
      label.length > MAX_CATEGORY_LABEL_LENGTH
        ? `${label.slice(0, MAX_CATEGORY_LABEL_LENGTH - 1)}…`
        : label;
    const count = seen.get(truncated) ?? 0;
    seen.set(truncated, count + 1);

    return truncated + '\u200B'.repeat(count);
  });
});

const values = computed(() => props.rows.map(([, value]) => value));

const hoverData = computed(() =>
  props.rows.map(([, value], index) => [fullCategories.value[index], props.formatValue(value)])
);

const resolvedHeight = computed(() => {
  if (props.height) {
    return props.height;
  }

  if (props.horizontal) {
    return `${Math.max(200, props.rows.length * HORIZONTAL_ROW_HEIGHT + 72)}px`;
  }

  return '300px';
});

// Keeps bars slim instead of letting a few categories fill the whole band.
const bargap = computed(() => {
  if (props.horizontal) {
    return 0.38;
  }

  const count = props.rows.length;
  if (count <= 2) return 0.72;
  if (count <= 4) return 0.55;
  if (count <= 8) return 0.4;
  return 0.25;
});

const chartData = computed<Data[]>(() => {
  const trace = {
    type: 'bar',
    marker: {
      color: barColor.value,
    },
    customdata: hoverData.value,
    hovertemplate: `%{customdata[0]}<br>${
      props.horizontal ? props.xLabel : props.yLabel
    }: <b>%{customdata[1]}</b><extra></extra>`,
  };

  if (props.horizontal) {
    return [
      {
        ...trace,
        orientation: 'h',
        x: values.value,
        y: tickCategories.value,
      } as Data,
    ];
  }

  return [
    {
      ...trace,
      x: tickCategories.value,
      y: values.value,
    } as Data,
  ];
});

const chartLayout = computed<BarLayout>(() => {
  const valueAxisTitle = props.horizontal ? props.xLabel : props.yLabel;
  const gridColor = themeVars.value.dividerColor;
  const mutedText = themeVars.value.textColor3;

  const valueAxis = {
    title: {
      text: valueAxisTitle,
      font: { size: 12, color: mutedText },
    },
    automargin: true,
    gridcolor: gridColor,
    zerolinecolor: gridColor,
    tickfont: { size: 12, color: mutedText },
    ...(props.valueTicks === undefined
      ? {}
      : { tickvals: props.valueTicks.tickvals, ticktext: props.valueTicks.ticktext }),
  };

  const categoryAxis = {
    automargin: true,
    showgrid: false,
    zeroline: false,
    tickfont: { size: 12, color: mutedText },
    tickangle: !props.horizontal && props.rows.length > 8 ? -35 : 0,
  };

  return {
    showlegend: false,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    bargap: bargap.value,
    barcornerradius: 4,
    font: {
      color: themeVars.value.textColor2,
      family: themeVars.value.fontFamily,
    },
    margin: { t: 8, r: 16, b: 44, l: 8 },
    hoverlabel: {
      font: { family: themeVars.value.fontFamily },
    },
    xaxis: props.horizontal ? valueAxis : categoryAxis,
    yaxis: props.horizontal ? categoryAxis : valueAxis,
  };
});

const chartConfig = computed<Partial<Config>>(() => ({
  displayModeBar: false,
  scrollZoom: false,
}));
</script>

<template>
  <n-card :title="title" size="small">
    <PlotlyPlot
      v-if="rows.length > 0"
      :data="chartData"
      :layout="chartLayout"
      :config="chartConfig"
      :height="resolvedHeight"
    />
    <n-empty v-else :description="emptyDescription" />
  </n-card>
</template>
